// File: frontend/src/components/ValuationFormScreen.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import ValuationFrame from "./ValuationFrame";
import { trackFunnelAction, trackFunnelError, trackFunnelStepView } from "../utils/analytics";

const DEFAULT_FORM_VALUES = {
  numberOfRooms: "100",
  daysOpen: "365",
  avgOccupancy: "70",
  adr: "100",
  roomRevenue: "2555000",
  totalRevenue: "3406667",
  operatingExpenses: "2384667",
  gop: "1022000",
  managementFee: "102200",
  propertyTax: "34067",
  insurance: "8517",
  ffeReserve: "102200",
  rent: "0",
  noi: "775016",
  maintenanceCapex: "0",
  expectedGrowth: "stabilized",
  expectedGrowthRate: "3",
  serviceType: "full-service",
  capRateApproach: "moderate",
  estimateTotalRevenue: false,
  gopMode: "percent",
  gopValue: "30",
  estimateManagementFee: false,
  managementFeeMode: "percent",
  managementFeeValue: "3",
  estimateFfe: false,
  ffeMode: "percent",
  ffeValue: "3",
};

const AUTO_FIELDS = [
  "roomRevenue",
  "totalRevenue",
  "operatingExpenses",
  "gop",
  "managementFee",
  "ffeReserve",
  "noi",
];

function ValuationFormScreen() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    propertyType: "hotel",
    type: "",
    region: "",
    city: "",
    marketPopulationBand: "",
    category: "",
    model: "Long form",
    country: "",
    currency: "",
    numberOfRooms: DEFAULT_FORM_VALUES.numberOfRooms,
    daysOpen: DEFAULT_FORM_VALUES.daysOpen,
    avgOccupancy: DEFAULT_FORM_VALUES.avgOccupancy,
    adr: DEFAULT_FORM_VALUES.adr,
    roomRevenue: DEFAULT_FORM_VALUES.roomRevenue,
    totalRevenue: DEFAULT_FORM_VALUES.totalRevenue,
    operatingExpenses: DEFAULT_FORM_VALUES.operatingExpenses,
    gop: DEFAULT_FORM_VALUES.gop,
    managementFee: DEFAULT_FORM_VALUES.managementFee,
    propertyTax: DEFAULT_FORM_VALUES.propertyTax,
    insurance: DEFAULT_FORM_VALUES.insurance,
    ffeReserve: DEFAULT_FORM_VALUES.ffeReserve,
    rent: DEFAULT_FORM_VALUES.rent,
    noi: DEFAULT_FORM_VALUES.noi,
    maintenanceCapex: DEFAULT_FORM_VALUES.maintenanceCapex,
    expectedGrowth: DEFAULT_FORM_VALUES.expectedGrowth,
    expectedGrowthRate: DEFAULT_FORM_VALUES.expectedGrowthRate,
    revenueGrowthNextYear: "",
    revenueGrowthYearAfter: "",
    revenueGrowthThirdYear: "",
    targetGopMargin: "",
    capexOption: "<20K per room",
    email: "",
    serviceType: DEFAULT_FORM_VALUES.serviceType,
    capRateApproach: DEFAULT_FORM_VALUES.capRateApproach,
    estimateTotalRevenue: DEFAULT_FORM_VALUES.estimateTotalRevenue,
    gopMode: DEFAULT_FORM_VALUES.gopMode,
    gopValue: DEFAULT_FORM_VALUES.gopValue,
    estimateManagementFee: DEFAULT_FORM_VALUES.estimateManagementFee,
    managementFeeMode: DEFAULT_FORM_VALUES.managementFeeMode,
    managementFeeValue: DEFAULT_FORM_VALUES.managementFeeValue,
    estimateFfe: DEFAULT_FORM_VALUES.estimateFfe,
    ffeMode: DEFAULT_FORM_VALUES.ffeMode,
    ffeValue: DEFAULT_FORM_VALUES.ffeValue,
    locationChoice: "",
    demandDriversChoice: "",
    publicTransportChoice: "",
    capexCondition: "",
    brandOperated: false,
    hasEnergyCertificate: false,
  });

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [cityDataExists, setCityDataExists] = useState(false);

  const populationBandLabel = (value) => {
    switch (value) {
      case "250k_plus":
        return "Greater than 250k population";
      case "100k_250k":
        return "100k to 250k population";
      case "60k_100k":
        return "60k to 100k population";
      case "30k_60k":
        return "30k to 60k population";
      case "under_30k":
        return "Less than 30k population";
      default:
        return value || "-";
    }
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return NaN;
    return parseFloat(String(value).replace(/,/g, ""));
  };

  const formatNumber = (num) => {
    if (!Number.isFinite(num)) return "--";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const currencyForCountry = (country) => {
    return String(country || "").toLowerCase().includes("ireland")
      ? "EUR"
      : "USD";
  };

  const localeForCurrency = (currency) => {
    return currency === "EUR" ? "en-IE" : "en-US";
  };

  const currentCurrency = () => {
    return formData.currency || currencyForCountry(formData.country);
  };

  const currencySymbol = () => {
    return currentCurrency() === "EUR" ? "€" : "$";
  };

  const formatCurrency = (num) => {
    if (!Number.isFinite(num)) return "--";
    const currency = currentCurrency();

    return new Intl.NumberFormat(localeForCurrency(currency), {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const toPlainNumberString = (num) => {
    if (!Number.isFinite(num)) return "";
    return Number.isInteger(num) ? String(num) : String(Number(num.toFixed(2)));
  };

  const calculateLineItem = (mode, value, totalRevenue) => {
    const numericValue = parseNumber(value);
    if (!Number.isFinite(numericValue)) return NaN;

    if (mode === "percent") {
      if (!Number.isFinite(totalRevenue)) return NaN;
      return totalRevenue * (numericValue / 100);
    }

    return numericValue;
  };

  const calculateRoomRevenueValue = (data) => {
    const days = parseNumber(data.daysOpen);
    const rooms = parseNumber(data.numberOfRooms);
    const occupancy = parseNumber(data.avgOccupancy);
    const adr = parseNumber(data.adr);

    if ([days, rooms, occupancy, adr].some((value) => !Number.isFinite(value))) {
      return NaN;
    }

    return days * rooms * (occupancy / 100) * adr;
  };

  const calculateTotalRevenueValue = (data, roomRevenue) => {
    const totalRevenue = parseNumber(data.totalRevenue);

    if (data.estimateTotalRevenue && Number.isFinite(roomRevenue)) {
      return roomRevenue > 0 ? roomRevenue / 0.75 : 0;
    }

    return totalRevenue;
  };

  const calculateGopValue = (data, totalRevenue) => {
    return calculateLineItem(data.gopMode, data.gopValue, totalRevenue);
  };

  const calculateManagementFeeValue = (data, totalRevenue) => {
    if (data.estimateManagementFee) {
      return Number.isFinite(totalRevenue) ? totalRevenue * 0.03 : NaN;
    }

    return calculateLineItem(
      data.managementFeeMode,
      data.managementFeeValue,
      totalRevenue
    );
  };

  const calculateFfeReserveValue = (data, totalRevenue) => {
    if (data.estimateFfe) {
      return Number.isFinite(totalRevenue) ? totalRevenue * 0.03 : NaN;
    }

    return calculateLineItem(data.ffeMode, data.ffeValue, totalRevenue);
  };

  const calculateNoiValue = ({
    gop,
    managementFee,
    rent,
    propertyTax,
    insurance,
    ffeReserve,
  }) => {
    if (!Number.isFinite(gop)) return NaN;

    return (
      gop -
      (Number.isFinite(managementFee) ? managementFee : 0) -
      (Number.isFinite(rent) ? rent : 0) -
      (Number.isFinite(propertyTax) ? propertyTax : 0) -
      (Number.isFinite(insurance) ? insurance : 0) -
      (Number.isFinite(ffeReserve) ? ffeReserve : 0)
    );
  };

  const withAutoCalculatedFields = (data) => {
    const next = { ...data };
    const roomRevenue = calculateRoomRevenueValue(next);
    const totalRevenue = calculateTotalRevenueValue(next, roomRevenue);
    const gop = calculateGopValue(next, totalRevenue);
    const managementFee = calculateManagementFeeValue(next, totalRevenue);
    const ffeReserve = calculateFfeReserveValue(next, totalRevenue);
    const rent = parseNumber(next.rent);
    const propertyTax = parseNumber(next.propertyTax);
    const insurance = parseNumber(next.insurance);
    const noi = calculateNoiValue({
      gop,
      managementFee,
      rent,
      propertyTax,
      insurance,
      ffeReserve,
    });

    if (Number.isFinite(roomRevenue)) {
      next.roomRevenue = toPlainNumberString(roomRevenue);
    }

    if (Number.isFinite(totalRevenue)) {
      next.totalRevenue = toPlainNumberString(totalRevenue);
    }

    if (Number.isFinite(gop)) {
      next.gop = toPlainNumberString(gop);
      next.operatingExpenses = toPlainNumberString(totalRevenue - gop);
    }

    if (Number.isFinite(managementFee)) {
      next.managementFee = toPlainNumberString(managementFee);
    }

    if (Number.isFinite(ffeReserve)) {
      next.ffeReserve = toPlainNumberString(ffeReserve);
    }

    if (Number.isFinite(noi)) {
      next.noi = toPlainNumberString(noi);
    }

    return next;
  };

  const calculatedSnapshot = () => {
    const snapshot = withAutoCalculatedFields({
      ...formData,
      currency: currentCurrency(),
    });

    return {
      roomRevenue: parseNumber(snapshot.roomRevenue),
      totalRevenue: parseNumber(snapshot.totalRevenue),
      operatingExpenses: parseNumber(snapshot.operatingExpenses),
      gop: parseNumber(snapshot.gop),
      managementFee: parseNumber(snapshot.managementFee),
      ffeReserve: parseNumber(snapshot.ffeReserve),
      noi: parseNumber(snapshot.noi),
    };
  };

  const formatFieldValue = (field, value) => {
    if (field.suffix === "%") {
      return `${value.toFixed(2)}%`;
    }

    if (field.format === "currency") {
      return formatCurrency(value);
    }

    return formatNumber(value);
  };

  const controlLabel = (fieldName, isEstimated = false) => {
    if (isEstimated) return "Estimated";
    return touchedFields[fieldName] ? "Entered" : "Default";
  };

  const enteredValueLabel = (field) => {
    if (field.type === "email") {
      return null;
    }

    const numericVal = parseNumber(formData[field.name]);
    if (Number.isFinite(numericVal)) {
      return {
        label: controlLabel(field.name),
        value: formatFieldValue(field, numericVal),
      };
    }

    const defaultValue = parseNumber(field.defaultValue || field.placeholder);
    if (Number.isFinite(defaultValue)) {
      return {
        label: "Default",
        value: formatFieldValue(field, defaultValue),
      };
    }

    return {
      label: "Default",
      value: "not entered",
    };
  };

  const enteredModeValueLabel = ({ name, modeName, estimateName }) => {
    const value = parseNumber(formData[name]);
    const mode = formData[modeName];
    const estimated = estimateName && formData[estimateName];

    if (!Number.isFinite(value)) {
      return {
        label: controlLabel(name, estimated),
        value: "not entered",
      };
    }

    return {
      label: controlLabel(name, estimated),
      value: mode === "percent" ? `${value.toFixed(2)}%` : formatCurrency(value),
    };
  };

  useEffect(() => {
    const type = localStorage.getItem("type") || "Operating/Stabilized";
    const country = localStorage.getItem("country") || "";
    const region = localStorage.getItem("region") || "";
    const city = localStorage.getItem("city") || "";
    const marketPopulationBand =
      localStorage.getItem("marketPopulationBand") || "";
    const category = localStorage.getItem("category") || "";
    const locationChoice = localStorage.getItem("locationChoice") || "";
    const demandDriversChoice =
      localStorage.getItem("demandDriversChoice") || "";
    const publicTransportChoice =
      localStorage.getItem("publicTransportChoice") || "";
    const capexCondition = localStorage.getItem("capexCondition") || "";
    const brandOperated = localStorage.getItem("brandOperated") === "true";
    const hasEnergyCertificate =
      localStorage.getItem("hasEnergyCertificate") === "true";

    setFormData((prev) =>
      withAutoCalculatedFields({
        ...prev,
        type,
        country,
        region,
        city,
        marketPopulationBand,
        category,
        currency: currencyForCountry(country),
        locationChoice,
        demandDriversChoice,
        publicTransportChoice,
        capexCondition,
        brandOperated,
        hasEnergyCertificate,
      })
    );
  }, []);

  useEffect(() => {
    trackFunnelStepView(4, "valuation_form");
  }, []);

  useEffect(() => {
    const currency = currencyForCountry(formData.country);
    setFormData((prev) => {
      if (prev.currency === currency) return prev;
      return { ...prev, currency };
    });
  }, [formData.country]);

  useEffect(() => {
    setFormData((prev) => {
      const next = withAutoCalculatedFields(prev);
      const changed = AUTO_FIELDS.some((field) => next[field] !== prev[field]);
      return changed ? next : prev;
    });
  }, [
    formData.daysOpen,
    formData.numberOfRooms,
    formData.avgOccupancy,
    formData.adr,
    formData.estimateTotalRevenue,
    formData.totalRevenue,
    formData.gopMode,
    formData.gopValue,
    formData.estimateManagementFee,
    formData.managementFeeMode,
    formData.managementFeeValue,
    formData.rent,
    formData.propertyTax,
    formData.insurance,
    formData.estimateFfe,
    formData.ffeMode,
    formData.ffeValue,
  ]);

  useEffect(() => {
    const { country, region, city } = formData;
    if (!city) {
      setCityDataExists(false);
      return;
    }

    axios
      .get(`/api/cities/check?country=${country}&region=${region}&city=${city}`)
      .then((res) => {
        setCityDataExists(Boolean(res.data.exists));
      })
      .catch(() => {
        setCityDataExists(false);
      });
  }, [formData.country, formData.region, formData.city]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value.replace(/,/g, "");

    setFormData((prev) =>
      withAutoCalculatedFields({
        ...prev,
        [name]: nextValue,
      })
    );
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    setErrors({});
  };

  const handleGrowthSelection = (growthType) => {
    if (growthType === "stabilized") {
      setFormData((prev) => ({
        ...prev,
        expectedGrowth: "stabilized",
        expectedGrowthRate: DEFAULT_FORM_VALUES.expectedGrowthRate,
        revenueGrowthNextYear: "",
        revenueGrowthYearAfter: "",
        revenueGrowthThirdYear: "",
        targetGopMargin: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        expectedGrowth: "expected",
        expectedGrowthRate: "",
      }));
    }
    setTouchedFields((prev) => ({ ...prev, expectedGrowth: true }));
    setErrors({});
  };

  const handleCaptcha = (val) => {
    if (typeof val === "string" && val.trim()) {
      setCaptchaVerified(true);
      setCaptchaToken(val);
    } else {
      setCaptchaVerified(false);
      setCaptchaToken("");
    }
  };

  const validate = (data = formData) => {
    const newErrors = {};
    const daysOpen = parseNumber(data.daysOpen);
    const numberOfRooms = parseNumber(data.numberOfRooms);
    const occupancy = parseNumber(data.avgOccupancy);
    const adr = parseNumber(data.adr);
    const totalRevenue = parseNumber(data.totalRevenue);
    const gop = parseNumber(data.gop);

    if (!data.type) newErrors.type = "Type is required.";
    if (!data.region) newErrors.region = "Region is required.";
    if (!data.city) newErrors.city = "City is required.";
    if (!data.category) newErrors.category = "Category is required.";
    if (!data.country) newErrors.country = "Country is required.";
    if (!data.totalRevenue) newErrors.totalRevenue = "Total revenue is required.";
    if (!data.gop) newErrors.gopValue = "GOP is required.";
    if (!data.noi) newErrors.noi = "NOI could not be calculated.";
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "A valid email is required.";
    }
    if (!Number.isFinite(daysOpen) || daysOpen <= 0) {
      newErrors.daysOpen = "Days open must be greater than zero.";
    }
    if (!Number.isFinite(numberOfRooms) || numberOfRooms <= 0) {
      newErrors.numberOfRooms = "Rooms must be greater than zero.";
    }
    if (
      !Number.isFinite(occupancy) ||
      occupancy < 0 ||
      occupancy > 100
    ) {
      newErrors.avgOccupancy = "Occupancy must be between 0% and 100%.";
    }
    if (!Number.isFinite(adr) || adr < 0) {
      newErrors.adr = "ADR cannot be negative.";
    }
    [
      ["totalRevenue", "Total revenue"],
      ["gop", "GOP"],
      ["operatingExpenses", "Operating expenses"],
      ["managementFee", "Management fee"],
      ["rent", "Rent"],
      ["propertyTax", "Property tax"],
      ["insurance", "Property insurance"],
      ["ffeReserve", "FF&E reserve"],
      ["maintenanceCapex", "Maintenance CAPEX"],
    ].forEach(([name, label]) => {
      const value = parseNumber(data[name]);
      if (Number.isFinite(value) && value < 0) {
        const errorField =
          name === "gop"
            ? "gopValue"
            : name === "managementFee"
            ? "managementFeeValue"
            : name === "ffeReserve"
            ? "ffeValue"
            : name;
        newErrors[errorField] = `${label} cannot be negative.`;
      }
    });
    if (
      Number.isFinite(totalRevenue) &&
      Number.isFinite(gop) &&
      gop > totalRevenue
    ) {
      newErrors.gopValue = "GOP cannot exceed total revenue.";
    }

    if (!data.expectedGrowth) {
      newErrors.expectedGrowth = "Please select an expected growth option.";
    } else if (data.expectedGrowth === "expected") {
      if (!data.revenueGrowthNextYear) {
        newErrors.revenueGrowthNextYear =
          "Revenue growth next year is required.";
      }
      if (!data.revenueGrowthYearAfter) {
        newErrors.revenueGrowthYearAfter =
          "Revenue growth year after is required.";
      }
      if (!data.revenueGrowthThirdYear) {
        newErrors.revenueGrowthThirdYear =
          "Revenue growth third year is required.";
      }
      if (!data.targetGopMargin) {
        newErrors.targetGopMargin = "Target GOP margin is required.";
      }
    } else if (data.expectedGrowth === "stabilized") {
      if (!data.expectedGrowthRate) {
        newErrors.expectedGrowthRate = "Stabilized growth rate is required.";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submissionData = withAutoCalculatedFields({
      ...formData,
      currency: currentCurrency(),
    });
    setFormData(submissionData);

    trackFunnelAction("valuation_submit_attempt", {
      step_number: 4,
      step_label: "valuation_form",
    });

    if (!captchaVerified) {
      trackFunnelError(4, "valuation_form", "captcha_missing");
      alert("Please complete the CAPTCHA.");
      return;
    }

    if (!validate(submissionData)) {
      trackFunnelError(4, "valuation_form", "validation_failed");
      alert("Please correct the highlighted errors.");
      return;
    }

    trackFunnelAction("form_submit", {
      step_number: 4,
      step_label: "valuation_form",
    });

    try {
      const response = await axios.post("/api/valuation", {
        ...submissionData,
        token: captchaToken,
      });
      const { docId, followUpToken } = response.data;
      trackFunnelAction("valuation_submit_success", {
        step_number: 4,
        step_label: "valuation_form",
        has_follow_up_token: Boolean(followUpToken),
      });
      const params = new URLSearchParams();
      if (docId) params.set("docId", docId);
      if (followUpToken) params.set("followUpToken", followUpToken);

      navigate(`/thank-you?${params.toString()}`, {
        state: { docId, followUpToken, email: submissionData.email },
      });
    } catch (error) {
      trackFunnelError(4, "valuation_form", "valuation_api_error");
      console.error("Error submitting data:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const renderTextField = (field) => {
    const isPercent = field.suffix === "%";
    const enteredValue = enteredValueLabel(field);

    return (
      <label className="form-label field-inline" key={field.name}>
        <div className="field-copy">
          <div className="label-text-container">
            <span className="label-text">
              {field.label}
              {field.required && <span className="required-mark"> *</span>}
            </span>
            {field.tooltip && (
              <>
                <span
                  data-tooltip-id={`tooltip-${field.name}`}
                  data-tooltip-content={field.tooltip}
                  className="tooltip-icon"
                >
                  ?
                </span>
                <Tooltip
                  id={`tooltip-${field.name}`}
                  place="top"
                  effect="solid"
                />
              </>
            )}
          </div>
          {field.helper && <p className="helper">{field.helper}</p>}
        </div>

        <div>
          <div className={isPercent ? "input-wrap" : ""}>
            <input
              type={field.type || "text"}
              name={field.name}
              inputMode={field.type === "email" ? undefined : "decimal"}
              value={formData[field.name]}
              onChange={handleChange}
              className={`input-field ${field.inputClassName || ""}`}
              placeholder={field.placeholder || field.defaultValue || ""}
              disabled={field.disabled || false}
            />
            {isPercent && <span className="input-suffix">%</span>}
          </div>
          {enteredValue && (
            <p className="helper entered-value">
              {enteredValue.label}: {enteredValue.value}
            </p>
          )}
          {errors[field.name] && (
            <p className="error-message">{errors[field.name]}</p>
          )}
        </div>
      </label>
    );
  };

  const renderComputedField = ({ label, helper, value, errorName }) => (
    <div className="field field-inline">
      <div className="field-copy">
        <span className="label-text">{label}</span>
        {helper && <p className="helper">{helper}</p>}
      </div>
      <div>
        <div className="readonly-value">{value}</div>
        {errors[errorName] && (
          <p className="error-message">{errors[errorName]}</p>
        )}
      </div>
    </div>
  );

  const renderModeAmountField = ({
    label,
    helper,
    modeName,
    valueName,
    estimateName,
    estimateLabel,
    amountValue,
    amountLabel,
  }) => {
    const valueLabel = enteredModeValueLabel({
      name: valueName,
      modeName,
      estimateName,
    });
    const mode = formData[modeName];
    const isEstimated = estimateName && formData[estimateName];

    return (
      <div className="field field-inline">
        <div className="field-copy">
          <span className="label-text">{label}</span>
          {helper && <p className="helper">{helper}</p>}
        </div>
        <div className="control-stack">
          {estimateName && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                name={estimateName}
                checked={Boolean(formData[estimateName])}
                onChange={handleChange}
              />
              {estimateLabel}
            </label>
          )}
          <select
            name={modeName}
            value={mode}
            onChange={handleChange}
            className="select-field"
            disabled={Boolean(isEstimated)}
          >
            <option value="percent">% of total revenue</option>
            <option value="absolute">Absolute value</option>
          </select>
          <div className="input-wrap">
            <input
              type="text"
              name={valueName}
              inputMode="decimal"
              value={formData[valueName]}
              onChange={handleChange}
              className="input-field"
              disabled={Boolean(isEstimated)}
            />
            <span className="input-suffix">
              {mode === "percent" ? "%" : currencySymbol()}
            </span>
          </div>
          <p className="helper entered-value">
            {valueLabel.label}: {valueLabel.value}
          </p>
          {Number.isFinite(amountValue) && (
            <p className="helper entered-value">
              {amountLabel}: {formatCurrency(amountValue)}
            </p>
          )}
          {errors[valueName] && (
            <p className="error-message">{errors[valueName]}</p>
          )}
        </div>
      </div>
    );
  };

  const operatingFields = [
    {
      name: "daysOpen",
      label: "Days Open",
      tooltip: "Days open per year.",
      defaultValue: DEFAULT_FORM_VALUES.daysOpen,
    },
    {
      name: "numberOfRooms",
      label: "Rooms",
      tooltip: "Total rooms. Commas are okay.",
      defaultValue: DEFAULT_FORM_VALUES.numberOfRooms,
    },
    {
      name: "avgOccupancy",
      label: "Occupancy",
      suffix: "%",
      tooltip: "For example, enter 70 for 70%.",
      defaultValue: DEFAULT_FORM_VALUES.avgOccupancy,
    },
    {
      name: "adr",
      label: `ADR (${currencySymbol()})`,
      format: "currency",
      tooltip: "Average daily rate.",
      defaultValue: DEFAULT_FORM_VALUES.adr,
    },
  ];

  const baseMoneyFields = [
    {
      name: "rent",
      label: `Rent (${currencySymbol()})`,
      format: "currency",
      defaultValue: DEFAULT_FORM_VALUES.rent,
    },
    {
      name: "propertyTax",
      label: `Property Tax (${currencySymbol()})`,
      format: "currency",
      defaultValue: DEFAULT_FORM_VALUES.propertyTax,
    },
    {
      name: "insurance",
      label: `Property Insurance (${currencySymbol()})`,
      format: "currency",
      defaultValue: DEFAULT_FORM_VALUES.insurance,
    },
    {
      name: "maintenanceCapex",
      label: `Year-One Maintenance CAPEX (${currencySymbol()})`,
      format: "currency",
      helper:
        "Applied as a year-one cash-flow adjustment in the backend valuation.",
      defaultValue: DEFAULT_FORM_VALUES.maintenanceCapex,
    },
  ];

  const growthFields = [
    {
      name: "expectedGrowthRate",
      label: "Stabilized Growth Rate",
      suffix: "%",
      defaultValue: DEFAULT_FORM_VALUES.expectedGrowthRate,
    },
    {
      name: "revenueGrowthNextYear",
      label: "Revenue Growth Next Year",
      suffix: "%",
    },
    {
      name: "revenueGrowthYearAfter",
      label: "Revenue Growth Year After",
      suffix: "%",
    },
    {
      name: "revenueGrowthThirdYear",
      label: "Revenue Growth Third Year",
      suffix: "%",
    },
    {
      name: "targetGopMargin",
      label: "Target GOP Margin",
      suffix: "%",
    },
  ];

  const snapshot = calculatedSnapshot();

  return (
    <ValuationFrame
      currentStep={3}
      title="Operating Inputs"
      subtitle="Tell us how the hotel has been performing. Future assumptions come after the current operating picture is clear."
    >
      <form onSubmit={handleSubmit}>
        <section className="summary-grid">
          <article className="info-card">
            <p className="metric-label">Market</p>
            <p className="metric-value">{formData.city || "-"}</p>
          </article>
          <article className="info-card">
            <p className="metric-label">Category</p>
            <p className="metric-value">{formData.category || "-"}</p>
          </article>
          <article className="info-card">
            <p className="metric-label">City Data</p>
            <p className="metric-value">
              {cityDataExists ? "Available" : "Limited"}
            </p>
          </article>
          {!cityDataExists && formData.marketPopulationBand && (
            <article className="info-card">
              <p className="metric-label">Market Size</p>
              <p className="metric-value">
                {populationBandLabel(formData.marketPopulationBand)}
              </p>
            </article>
          )}
          <article className="info-card">
            <p className="metric-label">Currency</p>
            <p className="metric-value">{currentCurrency()}</p>
          </article>
        </section>

        <section className="fieldset">
          <h2 className="fieldset-title">Hotel Operating Data</h2>
          <div className="form-stack">
            {operatingFields.map(renderTextField)}

            {renderComputedField({
              label: `Room Revenue (${currencySymbol()})`,
              helper: "Calculated from days open, rooms, occupancy, and ADR.",
              value: formatCurrency(snapshot.roomRevenue),
              errorName: "roomRevenue",
            })}

            <div className="field field-inline">
              <div className="field-copy">
                <span className="label-text">
                  Total Revenue ({currencySymbol()})
                </span>
                <p className="helper">
                  Enter total annual revenue or estimate it from room revenue.
                </p>
              </div>
              <div className="control-stack">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    name="estimateTotalRevenue"
                    checked={Boolean(formData.estimateTotalRevenue)}
                    onChange={handleChange}
                  />
                  Estimate from room revenue
                </label>
                {renderTextField({
                  name: "totalRevenue",
                  label: "",
                  format: "currency",
                  disabled: Boolean(formData.estimateTotalRevenue),
                  defaultValue: DEFAULT_FORM_VALUES.totalRevenue,
                }).props.children[1]}
              </div>
            </div>
          </div>
        </section>

        <section className="fieldset">
          <h2 className="fieldset-title">
            Operating Profit and Below-the-Line Items
          </h2>
          <div className="form-stack">
            {renderModeAmountField({
              label: "GOP",
              helper:
                "Choose whether GOP is entered as a percentage of revenue or an absolute value.",
              modeName: "gopMode",
              valueName: "gopValue",
              amountValue: snapshot.gop,
              amountLabel: "GOP preview",
            })}

            {renderComputedField({
              label: `Operating Expenses (${currencySymbol()})`,
              helper: "Calculated as total revenue less GOP.",
              value: formatCurrency(snapshot.operatingExpenses),
              errorName: "operatingExpenses",
            })}

            {renderModeAmountField({
              label: "Management Fee",
              helper: "Use the estimate option to apply 3% of total revenue.",
              modeName: "managementFeeMode",
              valueName: "managementFeeValue",
              estimateName: "estimateManagementFee",
              estimateLabel: "Estimate from total revenue",
              amountValue: snapshot.managementFee,
              amountLabel: "Management fee preview",
            })}

            {baseMoneyFields
              .filter((field) => field.name !== "maintenanceCapex")
              .map(renderTextField)}

            {renderModeAmountField({
              label: "FF&E Reserve",
              helper: "Use the estimate option to apply 3% of total revenue.",
              modeName: "ffeMode",
              valueName: "ffeValue",
              estimateName: "estimateFfe",
              estimateLabel: "Estimate from total revenue",
              amountValue: snapshot.ffeReserve,
              amountLabel: "FF&E reserve preview",
            })}

            {renderComputedField({
              label: `NOI (${currencySymbol()})`,
              helper:
                "Calculated from GOP less management fee, rent, property tax, insurance, and FF&E reserve.",
              value: formatCurrency(snapshot.noi),
              errorName: "noi",
            })}

            {baseMoneyFields
              .filter((field) => field.name === "maintenanceCapex")
              .map(renderTextField)}
          </div>
        </section>

        <section className="fieldset">
          <h2 className="fieldset-title">Future Potential</h2>
          <div className="form-stack">
            <div className="field field-inline">
              <div className="field-copy">
                <span className="label-text">Operations Stabilized?</span>
                <p className="helper">
                  Select stabilized growth or add staged growth assumptions.
                </p>
              </div>
              <div className="radio-container">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="expectedGrowth"
                    value="stabilized"
                    checked={formData.expectedGrowth === "stabilized"}
                    onChange={() => handleGrowthSelection("stabilized")}
                  />
                  Yes, stabilized
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="expectedGrowth"
                    value="expected"
                    checked={formData.expectedGrowth === "expected"}
                    onChange={() => handleGrowthSelection("expected")}
                  />
                  No, add expected growth
                </label>
                {errors.expectedGrowth && (
                  <p className="error-message">{errors.expectedGrowth}</p>
                )}
              </div>
            </div>

            {formData.expectedGrowth === "stabilized" &&
              renderTextField(growthFields[0])}

            {formData.expectedGrowth === "expected" && (
              <>{growthFields.slice(1).map(renderTextField)}</>
            )}
          </div>
        </section>

        <section className="fieldset">
          <h2 className="fieldset-title">Contact and Submission</h2>
          <div className="form-stack">
            {renderTextField({
              name: "email",
              label: "Email",
              type: "email",
              inputClassName: "input-left",
              required: true,
            })}

            <div className="field field-inline">
              <div className="field-copy">
                <span className="label-text">Verification</span>
                <p className="helper">
                  Complete verification before submitting the valuation.
                </p>
              </div>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptcha}
              />
            </div>
          </div>
        </section>

        <div className="button-row">
          <button
            type="button"
            className="button secondary-button"
            onClick={() => navigate("/demand-drivers")}
          >
            Back
          </button>
          <button type="submit" className="submit-button">
            Submit Valuation
          </button>
        </div>
      </form>
    </ValuationFrame>
  );
}

export default ValuationFormScreen;
